/**
 * Patient Portal — English text constants
 * Single source of truth for all patient-facing strings
 */
export const t = {
  // Global
  appName: 'Khmer Health',
  support247: 'Khmer Health Vietnam · 24/7 Support',
  hello: 'Hello',
  logout: 'Log out',
  loading: 'Loading...',
  submitting: 'Submitting...',
  defaultGuestName: 'Guest',

  // Navigation
  nav: {
    overview: 'Overview',
    transport: 'Transport',
    hotels: 'Stays',
    tours: 'Tours',
    requests: 'Requests',
  },

  // Landing / Login
  landing: {
    title: 'Look up your services',
    subtitle: 'Enter your passport number and last 4 digits of your phone number to view your transport, hotel, tour, and request information.',
    passportLabel: 'Passport Number',
    passportPlaceholder: 'e.g. N12345678',
    phoneLast4Label: 'Last 4 digits of phone number',
    phoneLast4Placeholder: 'e.g. 1234',
    lookupButton: 'Look up',
    lookingUp: 'Looking up...',
    privacyNote: 'Your information is kept secure. Only someone with the correct passport and registered phone number can view services.',
    errorPassport: 'Please enter your passport number',
    errorPhone: 'Please enter exactly 4 digits',
    errorNotFound: 'No records found. Please check your passport number and phone number.',
    errorSystem: 'System error. Please try again.',
  },

  // Dashboard / Overview
  dashboard: {
    title: 'My Journey',
    subtitle: 'Your service information and change requests',
    upcomingServices: 'Upcoming Services',
    noServicesBooked: 'No services booked yet',
    whatToChange: 'What would you like to change?',
    recentRequests: 'Recent Requests',
    viewAllRequests: 'View all requests →',
    airportPickup: 'Airport Pickup',
    airportDropoff: 'Airport Drop-off',
    dailyRental: 'Daily Rental',
    hotel: 'Stay',
    tour: 'Tour',
    actionTransport: 'Request Transport Change',
    actionHotel: 'Stay Request',
    actionTour: 'Change or Cancel Tour',
    actionViewRequests: 'View Submitted Requests',
  },

  // Transport
  transport: {
    title: 'My Transport',
    noServices: 'No transport services booked',
    airportPickup: 'Airport Pickup',
    airportDropoff: 'Airport Drop-off',
    dailyRental: 'Daily Rental',
    oneWay: 'One Way Transfer',
    roundTrip: 'Round Trip',
    sameDayReturn: 'Same Day Return',
    transport: 'Transport',
    arrival: 'Arrival',
    departure: 'Departure',
    confirmed: 'Confirmed',
    pending: 'Pending',
    completed: 'Completed',
    driverAssigned: 'Driver Assigned',
    enRoute: 'En Route',
    pickedUp: 'Picked Up',
    pickupTime: 'Pickup time',
    date: 'Date:',
    time: 'Time:',
    trip: 'Trip:',
    route: 'Route',
    serviceArea: 'Service Area',
    driverInfo: 'Driver Information',
    driverName: 'Driver',
    driverPhone: 'Phone',
    licensePlate: 'License Plate',
    driverPending: 'Driver info pending',
    vendorName: 'Vendor',
    requestChange: 'Request Change',
    requestCancel: 'Request Cancellation',
    // Change modal
    changeTitle: 'Request Transport Change',
    changePlaceholder: 'Describe the change you need (e.g. different pickup time, different date...)',
    submitChange: 'Submit Change Request',
    changeSummary: 'Transport change request:',
    changeSuccess: 'Change request submitted',
    // Cancel modal
    cancelTitle: 'Request Cancellation',
    cancelPolicy: 'Cancellation Policy',
    policyBefore24h: 'More than 24 hours before: ',
    policyFree: 'No fee',
    policy24to12h: '24 to 12 hours before: ',
    policy50: '50% fee',
    policyWithin12h: 'Within 12 hours: ',
    policy100: '100% fee',
    estimatedFee: 'Estimated fee:',
    cancelPlaceholder: 'Reason for cancellation (optional)',
    policyAcceptLabel: 'I have read and agree to the cancellation policy. I understand the request will be reviewed by staff.',
    reviewNotice: 'Your request will be reviewed and responded to by our staff',
    submitCancel: 'Submit Cancellation Request',
    cancelSuccess: 'Cancellation request submitted',
    cancelSummary: 'Transport cancellation request:',
    bandFree: 'No fee',
    band50: '50% fee',
    band100: '100% fee',
  },

  // Hotels / Stays
  stays: {
    title: 'My Stays',
    noBookings: 'No stay bookings yet',
    hotel: 'Stay',
    unconfirmedHotel: 'Stay (unconfirmed)',
    confirmed: 'Confirmed',
    pending: 'Pending',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    nights: 'Nights',
    submitRequest: 'Submit Stay Request',
    modalTitle: 'Stay Request',
    hotelLabel: 'Stay:',
    datesLabel: 'Dates:',
    requestPlaceholder: 'Describe your request (e.g. extra room, extend stay, special request...)',
    submitButton: 'Submit Request',
    requestSummary: 'Stay request:',
    requestSuccess: 'Request submitted',
  },

  // Tours
  tours: {
    title: 'My Tours',
    noTours: 'No tours booked yet',
    confirmed: 'Confirmed',
    pending: 'Pending',
    reschedule: 'Reschedule',
    cancel: 'Cancel Tour',
    rescheduleTitle: 'Reschedule Tour',
    cancelTitle: 'Cancel Tour',
    tourLabel: 'Tour:',
    dateLabel: 'Date:',
    reschedulePlaceholder: 'Enter your preferred new date...',
    cancelPlaceholder: 'Reason for cancellation...',
    submitReschedule: 'Submit Reschedule Request',
    submitCancel: 'Submit Cancellation Request',
    rescheduleSummary: 'Reschedule tour:',
    cancelSummary: 'Cancel tour:',
    requestSuccess: 'Request submitted',
  },

  // Requests
  requests: {
    title: 'My Requests',
    noRequests: 'No requests yet',
    noRequestsHelper: 'Change requests you submit will appear here',
    cancellationFee: 'Cancellation fee:',
    response: 'Response:',
    statusLabels: {
      submitted: 'Pending Approval',
      reviewing: 'Pending Approval',
      pending_admin_approval: 'Pending Approval',
      under_review: 'Pending Approval',
      approved: 'Approved',
      approved_by_admin: 'Approved',
      sent_to_vendor: 'Approved',
      vendor_acknowledged: 'Approved',
      vendor_updated: 'Updated',
      completed: 'Updated',
      applied: 'Updated',
      rejected: 'Cannot Process',
      rejected_by_admin: 'Cannot Process',
      cancelled: 'Cancelled',
    } as Record<string, string>,
    statusHelpers: {
      submitted: 'Your request has been received and is waiting for staff review',
      reviewing: 'Staff is reviewing your request and will respond shortly',
      pending_admin_approval: 'Your request has been received and is waiting for staff review',
      under_review: 'Staff is reviewing your request and will respond shortly',
      approved: 'Your request has been approved',
      approved_by_admin: 'Your request has been approved',
      sent_to_vendor: 'Your request has been approved and sent to vendor',
      vendor_acknowledged: 'Vendor has acknowledged your request',
      vendor_updated: 'Your service has been updated as requested',
      completed: 'Your service has been updated as requested',
      applied: 'Your service has been updated as requested',
      rejected: 'Your request could not be processed — please see the response note',
      rejected_by_admin: 'Your request could not be processed — please see the response note',
    } as Record<string, string>,
    moduleLabels: {
      transport: 'Transport',
      stays: 'Stays',
      tours: 'Tours',
      interpreters: 'Interpreters',
    } as Record<string, string>,
  },
} as const;
